import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { RadioButtonProps } from '@bl/components';
import { requiredAndWithoutSpaces } from '@core/misc/required-without-spaces.validator';
import { useValidatorsIf } from '@core/misc/use-validators-if.validator';
import { ToastActions } from '@core/state/toast/toast.actions';
import { filter } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { ClientType } from 'src/app/core/state/types';
import { LundifySubscriptionFacade } from '../services/lundify-subscription.facade';
import { LundifySubscriptionFormControls, LundifySubscriptionFormType, PayingPart } from '../types';

@Component({
  selector: 'app-paying-part',
  templateUrl: './paying-part.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [getTranslationProvider('PayingPartComponent')],
})
export class PayingPartComponent extends TranslatableComponent implements OnInit {
  @Input() client: ClientType;
  @Input()
  set active(value: boolean) {
    if (!value) {
      this.formGroup.reset();
      this.lundifySubscriptionFacade.resetSubscriptionData();
    }
  }

  radioButtonsItems: RadioButtonProps[];
  showClientInformationSection: Signal<boolean>;
  saving: WritableSignal<boolean>;
  formGroup: FormGroup<LundifySubscriptionFormType>;

  private lundifySubscriptionFacade: LundifySubscriptionFacade;
  private formPayingPartValue: Signal<PayingPart>;
  private formOriginalPayingPartValue: Signal<PayingPart>;
  private formIsClientPayingSelected: Signal<boolean>;

  get isCollaboratorFirm(): Signal<boolean> {
    return this.lundifySubscriptionFacade.isCollaboratorFirm;
  }

  get updatePayingPartDisabledTooltip(): Signal<string | null> {
    // eslint-disable-next-line complexity
    return computed(() => {
      if (this.lundifySubscriptionFacade.isMiniOrAnnualBundle() === true) {
        return this.translate('miniOrAnnualPackageTooltip');
      }
      if (this.lundifySubscriptionFacade.isFirmSubscriptionType() === false) {
        return this.translate('onlyFirmSubscriptionTypeTooltip');
      }
      if (!this.lundifySubscriptionFacade.hasPermissionToEdit(this.client?.responsible?.id)()) {
        return this.translate('noPermissionToEditTooltip');
      }
      return null;
    });
  }

  constructor() {
    super();
    this.lundifySubscriptionFacade = inject(LundifySubscriptionFacade);
    this.formGroup = this.createForm();
    this.saving = signal(false);
    this.formPayingPartValue = this.createPayingPartSignal();
    this.formOriginalPayingPartValue = this.createOriginalPayingPartSignal();
    this.formIsClientPayingSelected = computed(() => this.formPayingPartValue() === 'client');
    this.showClientInformationSection = computed(
      () => this.formIsClientPayingSelected() && this.formPayingPartValue() !== this.formOriginalPayingPartValue()
    );
    this.setupFormValueChangesSubscription();

    // eslint-disable-next-line complexity
    effect(() => {
      if (
        !this.lundifySubscriptionFacade.hasSubscriptionData() ||
        !this.lundifySubscriptionFacade.isFirmSubscriptionType() ||
        this.lundifySubscriptionFacade.isArchived() ||
        this.lundifySubscriptionFacade.isCollaboratorFirm() ||
        this.lundifySubscriptionFacade.isMiniOrAnnualBundle() ||
        !this.lundifySubscriptionFacade.hasPermissionToEdit(this.client?.responsible?.id)()
      ) {
        this.formGroup.disable({ emitEvent: false });
        return;
      }
      this.formGroup.enable({ emitEvent: false });
    });

    toObservable(this.lundifySubscriptionFacade.hasSubscriptionData)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => {
        this.resetFormWithUpdatedValues(this.lundifySubscriptionFacade.payingPart());
      });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.resetFormWithUpdatedValues(this.lundifySubscriptionFacade.payingPart());
  }

  protected override onTranslationsLoaded(): void {
    this.setupRadioButtons();
  }

  revalidateForm(parentFormGroup?: FormGroup): void {
    const formGroupToValidate = parentFormGroup || this.formGroup;

    // update validity of all child controls
    Object.keys(formGroupToValidate.controls).forEach((key) => {
      const control = formGroupToValidate.controls[key];
      if (control instanceof FormGroup) {
        this.revalidateForm(control);
        return;
      }
      control.updateValueAndValidity();
    });
  }

  updatePayingPart(): void {
    if (this.formGroup.invalid || !this.formGroup.touched) {
      return;
    }

    this.saving.set(true);

    const payingPartValue = this.formPayingPartValue();
    const clientInformationValue = this.formGroup.get('clientInformation').value;

    const { reference, email, phoneNumber } = clientInformationValue;
    const body = payingPartValue === 'client' ? { reference, email, phoneNumber } : null;

    this.lundifySubscriptionFacade.updatePayingPart(this.client.id, body).subscribe({
      next: () => {
        this.resetFormWithUpdatedValues();
        this.saving.set(false);
        this.showSuccessToastMessage();
      },
      error: () => {
        this.saving.set(false);
        this.showErrorToastMessage();
      },
    });
  }

  private resetFormWithUpdatedValues(payingPart?: PayingPart): void {
    const payingPartValue = payingPart ?? this.formPayingPartValue();
    this.formGroup.reset();
    this.formGroup.patchValue({ originalPayingPart: payingPartValue, payingPart: payingPartValue });
    this.formGroup.markAsUntouched();
  }

  private setupFormValueChangesSubscription(): void {
    this.formGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (value.payingPart === value.originalPayingPart) {
        this.formGroup.markAsUntouched();
      }
    });
  }

  private getFirstContactNameOrEmpty(): string {
    return this.client?.contacts?.[0]?.label || '';
  }

  private createForm(): FormGroup<LundifySubscriptionFormType> {
    const useValidatorsPredicate = (control: AbstractControl) => {
      const grandParent = control.parent?.parent;
      const valueOf = (name: LundifySubscriptionFormControls) => grandParent?.get(name)?.value;

      return valueOf('payingPart') === 'client' && valueOf('originalPayingPart') !== 'client';
    };

    return new FormGroup<LundifySubscriptionFormType>({
      payingPart: new FormControl<PayingPart>(null),
      originalPayingPart: new FormControl<PayingPart>(null),
      clientInformation: new FormGroup({
        reference: new FormControl<string>(this.getFirstContactNameOrEmpty(), {
          validators: [useValidatorsIf(useValidatorsPredicate, requiredAndWithoutSpaces)],
        }),
        email: new FormControl<string>(this.client?.email || '', {
          validators: [useValidatorsIf(useValidatorsPredicate, [requiredAndWithoutSpaces, Validators.email])],
        }),
        phoneNumber: new FormControl<string>(this.client?.phone || '', {
          validators: [useValidatorsIf(useValidatorsPredicate, Validators.required)],
        }),
        acceptPaymentTerms: new FormControl<boolean>(false, {
          validators: [useValidatorsIf(useValidatorsPredicate, Validators.requiredTrue)],
        }),
        acceptInformedClient: new FormControl<boolean>(false, {
          validators: [useValidatorsIf(useValidatorsPredicate, Validators.requiredTrue)],
        }),
      }),
    });
  }

  private createPayingPartSignal(): Signal<PayingPart> {
    return this.formControlNameToSignal('payingPart');
  }

  private createOriginalPayingPartSignal(): Signal<PayingPart> {
    return this.formControlNameToSignal('originalPayingPart');
  }

  private formControlNameToSignal(controlName: LundifySubscriptionFormControls) {
    return toSignal(this.formGroup.get(controlName).valueChanges, {
      initialValue: this.formGroup.get(controlName).value,
    });
  }

  private setupRadioButtons(): void {
    this.radioButtonsItems = [
      {
        name: 'firm',
        value: 'firm',
        text: this.translate('firmPayingTitle'),
        description: this.translate('firmPayingDescription'),
      },
      {
        name: 'client',
        value: 'client',
        text: this.translate('clientPayingTitle'),
        description: this.translate('clientPayingDescription'),
      },
    ];
  }

  private showSuccessToastMessage(): void {
    this.store.dispatch(
      ToastActions.showInfoMessage({
        summary: this.translate('savedTitle'),
        detail: this.translate('savedDescription'),
      })
    );
  }

  private showErrorToastMessage(): void {
    this.store.dispatch(
      ToastActions.showErrorMessage({
        summary: this.translate('errorTitle'),
        detail: this.translate('errorDescription'),
      })
    );
  }
}
